/**
 * Tone for the "Actual" hours value relative to the estimate bands. Renders as a green /
 * amber / red color so the row tells you at a glance whether the work landed where AI
 * said it would, where the no-AI fallback said it would, or overran both.
 *
 *   actual ≤ AI-assisted max                          → 'good'  (within AI estimate)
 *   AI-assisted max < actual ≤ No-AI max              → 'amber' (between AI and no-AI)
 *   actual > No-AI max                                → 'bad'   (overran both)
 */
export type EffortTone = 'good' | 'amber' | 'bad' | undefined;

export function actualEffortTone(
  actual: number | null | undefined,
  aiMax: number | null | undefined,
  noAiMax: number | null | undefined,
): EffortTone {
  if (actual == null) return undefined;
  if (aiMax != null && actual <= aiMax) return 'good';
  if (noAiMax != null && actual <= noAiMax) return 'amber';
  // If both estimate maxes are missing we have nothing to compare against — no color.
  if (aiMax == null && noAiMax == null) return undefined;
  return 'bad';
}

export function effortToneClass(tone: EffortTone): string {
  if (!tone) return '';
  return `actual-tone-${tone}`;
}

/** For the Metric component, which already supports `accent` of 'good' | 'warn'. */
export function effortToneAccent(tone: EffortTone): 'good' | 'warn' | 'amber' | undefined {
  if (tone === 'good') return 'good';
  if (tone === 'amber') return 'amber';
  if (tone === 'bad') return 'warn';
  return undefined;
}

/** CSS class for an inline `.metric` wrapper that doesn't go through the Metric component. */
export function metricToneClass(tone: EffortTone): string {
  const accent = effortToneAccent(tone);
  return accent ? `metric metric-${accent}` : 'metric';
}

/**
 * Compare actual hours against the no-AI estimate range and render a label that tells you
 * whether the work saved hours, overran, or landed somewhere in between. This replaces the
 * old "Hours saved = no-AI estimate − AI-assisted estimate" formula, which compared two
 * projections and stayed positive even when reality blew past both estimates.
 *
 * Convention: positive = saved hours; negative = overrun.
 *   savedMin = noAiMin − actual
 *   savedMax = noAiMax − actual
 */
export interface ActualVsNoAiResult {
  label: string;
  accent: 'good' | 'warn' | 'amber' | undefined;
  /**
   * Hint suitable for the Metric component, explaining the math. Optional consumers
   * may ignore it.
   */
  hint: string;
}

export function actualVsNoAi(
  actual: number | null | undefined,
  noAiMin: number | null | undefined,
  noAiMax: number | null | undefined,
  trim: (n: number) => string,
): ActualVsNoAiResult {
  if (actual == null || noAiMin == null || noAiMax == null) {
    return {
      label: '—',
      accent: undefined,
      hint: 'Recorded actual hours and a no-AI estimate are both required to compare.',
    };
  }
  return savedHoursLabel(noAiMin - actual, noAiMax - actual, trim);
}

/**
 * Same comparison as [actualVsNoAi] but takes the already-computed saved deltas. The
 * dashboard backend pre-aggregates these (`hoursSavedMin/Max = noAi_for_tracked −
 * actual_total`) so we don't recompute them on the frontend — just label them.
 */
export function savedHoursLabel(
  savedMin: number,
  savedMax: number,
  trim: (n: number) => string,
): ActualVsNoAiResult {
  // Treat exact zeros on both sides as "no data" — that's what the backend uses when no
  // PRs have actuals recorded yet.
  if (savedMin === 0 && savedMax === 0) {
    return {
      label: '—',
      accent: undefined,
      hint: 'Recorded actual hours and a no-AI estimate are both required to compare.',
    };
  }
  if (savedMax < 0) {
    return {
      label: `Overrun ${trim(-savedMax)}–${trim(-savedMin)} h`,
      accent: 'warn',
      hint: 'Actual hours exceeded the no-AI estimate; AI did not save time on this slice.',
    };
  }
  if (savedMin < 0) {
    return {
      label: `${trim(savedMin)}…${trim(savedMax)} h`,
      accent: 'amber',
      hint: 'Actual hours fell inside the no-AI estimate range — savings depend on which end of the band you trust.',
    };
  }
  return {
    label: `Saved ${trim(savedMin)}–${trim(savedMax)} h`,
    accent: 'good',
    hint: 'no-AI estimate − actual hours. Positive means time saved versus the no-AI baseline.',
  };
}
